import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Moon, Plus, TrendingUp, TrendingDown, Minus, Star } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SleepLog } from '@/hooks/useSleepTracking';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

/** Health sleep from Apple Health (health_metrics.sleep_hours) */
export type HealthSleepEntry = { date: string; hours: number };

interface SleepTrackingCardProps {
  sleepLogs: SleepLog[];
  /** Apple Health sleep data - merged with manual logs (Health preferred when both exist) */
  healthSleepData?: HealthSleepEntry[];
  weeklyAverage: number;
  trend: 'improving' | 'declining' | 'stable';
  onLogSleep: (sleep: Omit<SleepLog, 'id'>) => void;
  /** When set, shows a favorite star (e.g. Health Data screen) */
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  /** Match Health graph cards when embedded in Favorites */
  variant?: 'default' | 'healthData';
}

export const SleepTrackingCard = ({
  sleepLogs,
  healthSleepData = [],
  weeklyAverage,
  trend,
  onLogSleep,
  isFavorite,
  onToggleFavorite,
  variant = 'default',
}: SleepTrackingCardProps) => {
  const { t } = useTranslation();
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [newSleepLog, setNewSleepLog] = useState({
    sleep_date: new Date().toISOString().split('T')[0],
    hours_slept: '8',
    quality_rating: 5,
    notes: ''
  });

  const handleLogSleep = () => {
    const hours = parseFloat(String(newSleepLog.hours_slept).replace(',', '.')) || 0;
    onLogSleep({
      ...newSleepLog,
      hours_slept: hours,
    });
    setNewSleepLog({
      sleep_date: new Date().toISOString().split('T')[0],
      hours_slept: '8',
      quality_rating: 5,
      notes: ''
    });
    setShowLogDialog(false);
  };

  const getTrendIcon = () => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'declining':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'improving':
        return 'text-green-500';
      case 'declining':
        return 'text-red-500';
      default:
        return 'text-yellow-500';
    }
  };

  // Merge Health + manual: prefer manual when available, else Health
  const mergedByDate = useMemo(() => {
    const map = new Map<string, number>();
    for (const h of healthSleepData) {
      map.set(h.date, h.hours);
    }
    for (const log of sleepLogs) {
      map.set(log.sleep_date, log.hours_slept); // Manual overwrites Health for same date
    }
    return map;
  }, [sleepLogs, healthSleepData]);

  const mergedSorted = useMemo(() => {
    return Array.from(mergedByDate.entries())
      .map(([date, hours]) => ({ date, hours }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [mergedByDate]);

  const showExtendedView = mergedSorted.length > 7;
  const daysToShow = showExtendedView ? 30 : 7;
  const periodLabel = showExtendedView
    ? t('components.sleepCard.average30')
    : t('components.sleepCard.average7');
  const chartLabel = showExtendedView
    ? t('components.sleepCard.last30Days')
    : t('components.sleepCard.last7Days');

  const chartData = mergedSorted.slice(0, daysToShow).reverse().map(({ date, hours }) => ({
    date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    hours
  }));

  // Use merged data for stats when we have it; else fall back to manual-only
  const displayWeeklyAverage = mergedSorted.length > 0
    ? Math.round((mergedSorted.slice(0, 7).reduce((s, x) => s + x.hours, 0) / Math.min(7, mergedSorted.length)) * 10) / 10
    : weeklyAverage;
  const displayTrend = mergedSorted.length >= 14
    ? (() => {
        const first7 = mergedSorted.slice(7, 14).reduce((s, x) => s + x.hours, 0) / 7;
        const last7 = mergedSorted.slice(0, 7).reduce((s, x) => s + x.hours, 0) / 7;
        if (last7 > first7 + 0.2) return 'improving' as const;
        if (last7 < first7 - 0.2) return 'declining' as const;
        return 'stable' as const;
      })()
    : trend;

  const cardClass =
    variant === 'healthData' ? 'bg-gradient-card border-0' : '';

  return (
    <Card className={cardClass}>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Moon className="h-5 w-5 text-primary" />
            {t('components.sleepCard.title')}
          </CardTitle>
          <div className="flex items-center gap-1 shrink-0">
            {onToggleFavorite && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite();
                }}
                aria-label={isFavorite ? t('components.sleepCard.removeFavorite') : t('components.sleepCard.addFavorite')}
              >
                <Star className={`h-4 w-4 ${isFavorite ? 'fill-primary text-primary' : ''}`} />
              </Button>
            )}
          <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                {t('components.sleepCard.logTitle')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('components.sleepCard.logTitle')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="sleepDate">{t('components.sleepCard.dateLabel')}</Label>
                  <Input
                    id="sleepDate"
                    type="date"
                    value={newSleepLog.sleep_date}
                    onChange={(e) => setNewSleepLog({...newSleepLog, sleep_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="hoursSlept">{t('components.sleepCard.hoursLabel')}</Label>
                  <Input
                    id="hoursSlept"
                    type="text"
                    inputMode="decimal"
                    placeholder={t('components.sleepCard.hoursPlaceholder')}
                    value={newSleepLog.hours_slept}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^[0-9]*[,.]?[0-9]*$/.test(v)) {
                        setNewSleepLog({...newSleepLog, hours_slept: v});
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div>
                  <Label htmlFor="quality">{t('components.sleepCard.qualityLabel')}</Label>
                  <Input
                    id="quality"
                    type="number"
                    min="1"
                    max="5"
                    value={newSleepLog.quality_rating || ''}
                    onChange={(e) => setNewSleepLog({...newSleepLog, quality_rating: parseInt(e.target.value) || 5})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div>
                  <Label htmlFor="notes">{t('components.sleepCard.notesLabel')}</Label>
                  <Input
                    id="notes"
                    value={newSleepLog.notes}
                    onChange={(e) => setNewSleepLog({...newSleepLog, notes: e.target.value})}
                    placeholder={t('components.sleepCard.notesPlaceholder')}
                  />
                </div>
                <Button onClick={handleLogSleep} className="w-full">
                  {t('components.sleepCard.submitLog')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Sleep Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">{displayWeeklyAverage}h</div>
              <p className="text-sm text-muted-foreground">{periodLabel}</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className={`flex items-center justify-center gap-2 ${displayTrend === 'improving' ? 'text-green-500' : displayTrend === 'declining' ? 'text-red-500' : 'text-yellow-500'}`}>
                {displayTrend === 'improving' ? <TrendingUp className="h-4 w-4" /> : displayTrend === 'declining' ? <TrendingDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                <span className="font-semibold">{t(`components.sleepCard.trends.${displayTrend}`)}</span>
              </div>
              <p className="text-sm text-muted-foreground">{t('components.sleepCard.trend')}</p>
            </div>
          </div>

          {/* Sleep Quality Indicator */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('components.sleepCard.sleepQuality')}</span>
              <span className="text-sm text-muted-foreground">
                {displayWeeklyAverage >= 8
                  ? t('components.sleepCard.quality.excellent')
                  : displayWeeklyAverage >= 7
                  ? t('components.sleepCard.quality.good')
                  : displayWeeklyAverage >= 6
                  ? t('components.sleepCard.quality.fair')
                  : t('components.sleepCard.quality.poor')}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  displayWeeklyAverage >= 8 ? 'bg-green-500' : 
                  displayWeeklyAverage >= 7 ? 'bg-yellow-500' : 
                  'bg-red-500'
                }`}
                style={{ 
                  width: `${Math.min((displayWeeklyAverage / 8) * 100, 100)}%`,
                  filter: `drop-shadow(0 0 8px ${
                    displayWeeklyAverage >= 8 ? '#22c55e' : 
                    displayWeeklyAverage >= 7 ? '#eab308' : 
                    '#ef4444'
                  }80) drop-shadow(0 0 4px ${
                    displayWeeklyAverage >= 8 ? '#22c55e' : 
                    displayWeeklyAverage >= 7 ? '#eab308' : 
                    '#ef4444'
                  })`
                }}
              />
            </div>
          </div>

          {/* Sleep Chart */}
          {chartData.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">{chartLabel}</h4>
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      dataKey="date" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      domain={[0, 12]}
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-background p-2 border rounded shadow-sm">
                              <p className="text-sm">{label}</p>
                              <p className="text-sm font-medium text-primary">
                                {t('components.sleepCard.tooltipHours', { value: payload[0].value })}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="hours" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {mergedSorted.length === 0 && (
            <div className="text-center py-8">
              <Moon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">{t('components.sleepCard.empty')}</p>
              <p className="text-sm text-muted-foreground">
                {t('components.sleepCard.emptyHint')}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};